/**
 * RBAC-3.4 · FR-2a, FR-2b, FR-2c, FR-5, UX-DR9. Adopt a Joules role: your own copy, with your people
 * moved onto it, in one step. Reversible while the copy is unedited.
 *
 * ⚠️ THE DATABASE IS THE ENFORCEMENT; THIS IS THE WORDING.
 *
 * `20261101c6_rbac_3_4_adopt_role.sql` decides who moves, makes the copy through RBAC-3.3's
 * `duplicate_role()` (one stripping rule), and refuses:
 *   - 22023 unless `p_expected_moving` is the number it is about to move, so the number this confirm
 *     states is the number that moves (FR-2a), and unless `p_acknowledged_dropped` is what it drops;
 *   - 42501 on Superadmin and Agency Admin, whose holders a copy would strip of system.override;
 *   - reversal (22023) once the copy's permissions or holders have changed (FR-2b).
 *
 * Who stays, and why, is the server's too: the caller, anyone who also belongs to another account
 * (their role is not per-account, so moving them would reach into it), and deactivated people.
 */

import { droppedPermissionLines, type DroppedPermissionLine } from './roleDuplication';
import type { AccountRole } from './accountRoles';

export const ROLE_ADOPTION_PREVIEW_RPC = 'role_adoption_preview';
export const ADOPT_ROLE_RPC = 'adopt_role';
export const ROLE_ADOPTION_STATUS_RPC = 'role_adoption_status';
export const REVERT_ROLE_ADOPTION_RPC = 'revert_role_adoption';

export interface AdoptionStaying {
  you: number;
  otherAccounts: number;
  inactive: number;
}

export interface RoleAdoptionPreview {
  sourceRoleId: number;
  sourceName: string;
  suggestedName: string;
  copiedCount: number;
  /** Reserved permission keys the copy will not carry, as the server compares them. */
  dropped: string[];
  /** The number of people who move. Sent back as the confirmation. */
  movingCount: number;
  staying: AdoptionStaying;
}

export interface AdoptRoleResult {
  roleId: number;
  name: string;
  adoptionId: number;
  sourceRoleId: number;
  movedCount: number;
}

export type RoleAdoptionStatus =
  | { adopted: false; roleId: number }
  | {
      adopted: true;
      roleId: number;
      adoptionId: number;
      sourceRoleId: number;
      sourceName: string;
      adoptedAt: string | null;
      movedCount: number;
      reversible: boolean;
      blockers: string[];
    };

export interface RevertAdoptionResult {
  movedBack: number;
  sourceName: string;
  sourceRoleId: number;
}

export interface AdoptConfirmationCopy {
  /** False when nobody would move: the dialog shows the summary and offers no Adopt button. */
  canAdopt: boolean;
  title: string;
  summary: string;
  losingIntro: string | null;
  losing: DroppedPermissionLine[];
  staying: string[];
  note: string;
  confirmLabel: string;
}

const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const people = (n: number) => (n === 1 ? '1 person' : `${n} people`);

export function parseAdoptionPreview(raw: unknown): RoleAdoptionPreview | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  // A missing count or dropped list must never read as "0 people" or "nothing dropped".
  if (typeof r.source_role_id !== 'number' || typeof r.source_name !== 'string') return null;
  if (typeof r.moving_count !== 'number' || !isStringArray(r.dropped)) return null;
  const s = (r.staying && typeof r.staying === 'object' ? r.staying : {}) as Record<string, unknown>;
  return {
    sourceRoleId: r.source_role_id,
    sourceName: r.source_name,
    suggestedName: typeof r.suggested_name === 'string' ? r.suggested_name : `${r.source_name} (copy)`,
    copiedCount: num(r.copied_count),
    dropped: r.dropped,
    movingCount: r.moving_count,
    staying: { you: num(s.you), otherAccounts: num(s.other_accounts), inactive: num(s.inactive) },
  };
}

export function parseAdoptResult(raw: unknown): AdoptRoleResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.role_id !== 'number' || typeof r.name !== 'string') return null;
  return {
    roleId: r.role_id,
    name: r.name,
    adoptionId: num(r.adoption_id),
    sourceRoleId: num(r.source_role_id),
    movedCount: num(r.moved_count),
  };
}

export function parseAdoptionStatus(raw: unknown): RoleAdoptionStatus | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.role_id !== 'number') return null;
  if (r.adopted !== true) return { adopted: false, roleId: r.role_id };
  return {
    adopted: true,
    roleId: r.role_id,
    adoptionId: num(r.adoption_id),
    sourceRoleId: num(r.source_role_id),
    sourceName: typeof r.source_name === 'string' ? r.source_name : 'the Joules role',
    adoptedAt: typeof r.adopted_at === 'string' ? r.adopted_at : null,
    movedCount: num(r.moved_count),
    // Only an explicit true offers the reversal.
    reversible: r.reversible === true,
    blockers: isStringArray(r.blockers) ? r.blockers : [],
  };
}

export function parseRevertResult(raw: unknown): RevertAdoptionResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.moved_back !== 'number') return null;
  return {
    movedBack: r.moved_back,
    sourceName: typeof r.source_name === 'string' ? r.source_name : '',
    sourceRoleId: num(r.source_role_id),
  };
}

function stayingLines(p: RoleAdoptionPreview): string[] {
  const out: string[] = [];
  if (p.staying.you > 0) out.push(`You stay on ${p.sourceName}. Another admin can move you onto the copy afterwards.`);
  if (p.staying.otherAccounts > 0) {
    const n = p.staying.otherAccounts;
    out.push(
      `${people(n)} stay${n === 1 ? 's' : ''} on ${p.sourceName} because they also belong to another account, ` +
        `and moving them would change what they can do there.`,
    );
  }
  if (p.staying.inactive > 0) {
    const n = p.staying.inactive;
    out.push(`${n === 1 ? '1 deactivated person' : `${n} deactivated people`} stay${n === 1 ? 's' : ''} on ${p.sourceName}.`);
  }
  return out;
}

export function adoptionConfirmationCopy(p: RoleAdoptionPreview): AdoptConfirmationCopy {
  const title = `Adopt ${p.sourceName}`;
  const staying = stayingLines(p);
  const note =
    "You can reverse this, moving the same people back and deleting the copy, until you change the copy's permissions or who holds it.";

  if (p.movingCount === 0) {
    return {
      canAdopt: false,
      title,
      summary: `Nobody in this account would move onto a copy of ${p.sourceName}. Use Duplicate to make your own copy instead.`,
      losingIntro: null,
      losing: [],
      staying,
      note: '',
      confirmLabel: 'Adopt',
    };
  }

  const n = p.movingCount;
  const d = p.dropped.length;
  return {
    canAdopt: true,
    title,
    summary:
      `Your account gets its own copy of ${p.sourceName}, and the ${people(n)} in this account who ` +
      `${n === 1 ? 'holds' : 'hold'} ${p.sourceName} ${n === 1 ? 'moves' : 'move'} onto it, in one step.`,
    losingIntro:
      d === 0
        ? null
        : `Only Joules can grant ${d} of its permissions, so the copy does not carry ${d === 1 ? 'it' : 'them'} ` +
          `and the ${people(n)} who move lose ${d === 1 ? 'it' : 'them'}:`,
    losing: droppedPermissionLines(p.dropped),
    staying,
    note,
    confirmLabel: `Adopt and move ${people(n)}`,
  };
}

/** The RPC arguments: the count and the dropped list are the preview's own, i.e. what was shown. */
export function adoptRoleArgs(p: RoleAdoptionPreview, name: string | null | undefined, description: string | null | undefined) {
  const n = (name ?? '').trim();
  const desc = (description ?? '').trim();
  return {
    p_source_role_id: p.sourceRoleId,
    p_name: n === '' ? null : n,
    p_description: desc === '' ? null : desc,
    p_acknowledged_dropped: [...p.dropped],
    p_expected_moving: p.movingCount,
  };
}

/** FR-5: Adopt is offered on Joules roles only, and not on the privileged two (the server refuses them too). */
export function canOfferAdopt(role: Pick<AccountRole, 'isGlobal' | 'isPrivileged'>): boolean {
  return role.isGlobal && !role.isPrivileged;
}

/** FR-2c / UX-DR9: what a Joules role says instead of offering Edit. Null on the account's own roles. */
export function joulesRoleGuidance(
  role: Pick<AccountRole, 'name' | 'isGlobal' | 'isPrivileged'>,
): { heading: string; lines: string[] } | null {
  if (!role.isGlobal) return null;
  const duplicate = 'Duplicate makes a copy and moves nobody.';
  return {
    heading: 'This is a Joules role, shared by every account. Adopt it to make it yours.',
    lines: role.isPrivileged
      ? [`${role.name} cannot be adopted: its holders would lose permissions only Joules can grant.`, duplicate]
      : ['Adopt makes your own copy and moves your people onto it, so you can change what they can do.', duplicate],
  };
}

export function revertConfirmationCopy(
  s: Extract<RoleAdoptionStatus, { adopted: true }>,
  copyName: string,
): { title: string; summary: string; confirmLabel: string } {
  return {
    title: `Reverse the adoption of ${s.sourceName}`,
    summary:
      `The ${people(s.movedCount)} moved onto ${copyName} go back to ${s.sourceName}, and ${copyName} is deleted. ` +
      'Everything is as it was before you adopted.',
    confirmLabel: `Move ${people(s.movedCount)} back`,
  };
}
